// eslint-disable-next-line import/no-named-as-default
import { useEffect, useMemo, useState } from "react";

import { Alert, Col, Form, Input, InputNumber, Modal, Row, Select, Switch } from "antd";
import axios, { AxiosError } from "axios";
import { EVA_RESEARCH_NAME, PAD_SCENARIO_SERVICE_ADDRESS } from "constants/app.constants";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";

import { useLengthConversion, useUser } from "hooks";
import usePlayTo3DDataPrivateSources from "hooks/usePlayTo3DDataPrivateSources";
import useUserModules from "hooks/useUserModules";

import { getValidSticks, getValidWells } from "api/data";

import { FilterScope, SavedFilterModel } from "models";

import { Stick } from "components/pad-generator/models/scenario";
import SegmentedToggle from "components/shared/SegmentedToggle";

import { PrimarySourceSelect } from "../PrimarySourceSelect";
import { useOrgScenarios } from "../hooks/useOrgScenarios";
import { usePutStickCalculationJob } from "../hooks/usePutStickCalculationJob";
import { StickCalculationsJob } from "../models/StickCalculations";
import { resolveStickJobEntities } from "../utils";

export const convertToTreeData = (data) => {
  return data.map((period) => ({
    title: period.period,
    value: `period_${period.period}`,
    key: `period_${period.period}`,
    type: `period`,
    checkable: false,
    children: period.plays.map((play) => ({
      title: play.play,
      value: `play_${play.play}`,
      key: `play_${play.play}`,
      type: `play`,
      children: play.zones.map((zone) => ({
        title: zone,
        value: zone,
        key: zone,
        type: `zone`
      }))
    }))
  }));
};

const FIELD_KEYS = {
  calculate3DV: "calculate3DV",
  calculateWellSpacing: "calculateWellSpacing",
  name: "name",
  wellList: "wellList",
  stickList: "stickList",
  zones: "zones",
  threeDvLeftWidth: "3dvLeftWidth",
  threeDvRightWidth: "3dvRightWidth",
  threeDvUpHeight: "3dvUpHeight",
  threeDvDownHeight: "3dvDownHeight",
  xdaLeftWidth: "xdaLeftWidth",
  xdaRightWidth: "xdaRightWidth",
  xdaUpHeight: "xdaUpHeight",
  xdaDownHeight: "xdaDownHeight"
};

const ZONE_MAP_PREFIX = "map_";

const STICK_API_URL = `${PAD_SCENARIO_SERVICE_ADDRESS}/api/v1/pad-scenario`;

const { TextArea } = Input;
export const CreateStickGeoCalculationJobModal = ({
  requestShowModal,
  jobs,
  onClose,
  savedFilters
}: CreateAccessTokenModalProps) => {
  const [isModalVisible, setIsModalVisible] = useState(requestShowModal);
  const [inputMode, setInputMode] = useState<"wellList" | "savedFilter">("wellList");
  const { email } = useUser();
  const [errors, setErrors] = useState<string>("");
  // const [periodKeys, setPeriodKeys] = useState([]);
  // const [selectedIpdbZones, setSelectedIpdbZones] = useState<string[]>([]);
  // const [treeData, setTreeData] = useState([]);

  // Use length conversion hook for all dimension fields
  const { convert, convertBack, getDisplayUnit } = useLengthConversion();

  const [form] = Form.useForm();
  const includeSampled3D = Form.useWatch(FIELD_KEYS.calculate3DV, form);

  const { mutateAsync } = usePutStickCalculationJob();

  const { data: orgScenarios, isLoading: isScenariosLoading } = useOrgScenarios(
    inputMode === "savedFilter"
  );

  // Build scenario options grouped by project name
  const scenarioOptions = useMemo(() => {
    if (!orgScenarios?.length) return [];
    const projectGroups = new Map<string, typeof orgScenarios>();
    for (const scenario of orgScenarios) {
      const key = scenario.projectName || "Unknown Project";
      const existing = projectGroups.get(key);
      if (!existing) {
        projectGroups.set(key, [scenario]);
      } else {
        existing.push(scenario);
      }
    }
    return Array.from(projectGroups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([projectName, scenarios]) => ({
        label: projectName,
        options: scenarios.map((s) => ({
          label: `${s.scenarioId}${s.padName ? ` - ${s.padName}` : ""}`,
          value: s._id.$oid
        }))
      }));
  }, [orgScenarios]);

  const [selectedScenarioIds, setSelectedScenarioIds] = useState<string[]>([]);

  // const { data: mapLayers } = getGeoMapLayers();

  const [modelSource, setModelSource] = useState("");

  const {
    data: modelSources,
    refetch: refetchDataSources
    // isLoading
  } = usePlayTo3DDataPrivateSources();

  useEffect(() => {
    refetchDataSources();
  }, []);

  // EVA-150: "EVA Research" is the McDaniel geo model, gated per play by TWO tiers — Geology
  // unlocks the XDA/spacing/parent-child columns, and 3D Geo Model additionally unlocks the
  // sampled-3D columns. Either tier makes the option worth offering, so only an org with neither
  // has it hidden; requiring 3D would wrongly deny a Geology-only org the columns it does pay for.
  // The org's own imported model sources are always listed.
  const { hasGeology, has3dGeoModel } = useUserModules();
  const geoSourceOptions = useMemo(
    () => [
      ...(hasGeology || has3dGeoModel ? [{ label: EVA_RESEARCH_NAME, value: "" }] : []),
      ...(modelSources || []).map((source) => ({
        label: source.source,
        value: source.source
      }))
    ],
    [hasGeology, has3dGeoModel, modelSources]
  );

  // EVA-150 (blocker from review): hiding the option does not change what gets submitted.
  // EVA_RESEARCH_NAME's value is the empty string and `modelSource` starts as "", so an org with
  // no Geology/3D subscription but with private models would open this modal, see the Select
  // render blank, never touch it, and still post modelSource: "" — the gated McDaniel source. The
  // backend fails closed so nothing leaks, but the user gets exactly the empty job this guard
  // exists to prevent. Keep the selection inside whatever the dropdown currently offers.
  useEffect(() => {
    if (!geoSourceOptions.some((option) => option.value === modelSource)) {
      const fallback = geoSourceOptions[0]?.value ?? "";
      setModelSource(fallback);
      form.setFieldsValue({ modelSource: fallback });
    }
  }, [geoSourceOptions, modelSource, form]);

  //   useEffect(() => {
  //     if (isLoading) return;

  //     if (modelSource === "") {
  //       getGroupedPlayZonesByPeriod().then((response) => {
  //         const treeData = convertToTreeData(response.data) ?? [];
  //         setPeriodKeys(response.data.map((p) => `period_${p.period}`));
  //         setTreeData(treeData);
  //       });
  //     } else if (modelSources?.length) {
  //       const selectedSourcePlays = modelSources.find(
  //         (source) => source.source === modelSource
  //       ).plays;
  //       const treeData = selectedSourcePlays.map((play) => ({
  //         title: play,
  //         value: `group_${play}`,
  //         key: `play_${play}`,
  //         type: `play`,
  //         checkable: false,
  //         children: mapLayers
  //           .filter((l) => l.group === play)
  //           .map((layer: MapLayer) => ({
  //             title: layer.name,
  //             value: `${layer._id}_map_${layer.name}`,
  //             key: `${layer._id}_map_${layer.name}`,
  //             type: `play`
  //           }))
  //       }));
  //       setTreeData(treeData);
  //     }
  //   }, [modelSource, modelSources, isLoading]);

  useEffect(() => {
    setIsModalVisible(requestShowModal);
    //updateProjects();
  }, [requestShowModal]);

  const handleOk = async () => {
    try {
      // EVA-150 (review): checked BEFORE validateFields - this is a precondition on the org,
      // not on anything the user typed, and an empty form would otherwise fail validation first
      // and hide it.
      // see CreateGeoCalculationJobModal — an empty option list means the
      // private-source request failed and the org holds neither tier, and the correction effect's
      // fallback is the gated source's value. Block submit rather than post it.
      if (geoSourceOptions.length === 0) {
        setErrors(
          "No geo source is available right now. Reload the page, or contact support if this persists."
        );
        return;
      }

      const values = await form.validateFields();

      const formattedName = (name) => name.trim().toLowerCase();
      const name = formattedName(values[FIELD_KEYS.name]);

      if (jobs.some((job) => formattedName(job.name) === name)) {
        setErrors("Job name already exists.");
        return;
      }

      if (
        values[FIELD_KEYS.calculate3DV] === false &&
        !values[FIELD_KEYS.calculateWellSpacing]
      ) {
        setErrors(
          "At least one calculation is required to proceed with the Geo Calculation job."
        );
        return;
      }

      let validWells: string[] = [];
      let savedFilterId: string | undefined;
      let savedFilterJson: string | undefined;
      let savedFilterName: string | undefined;
      let stickList: string[] = [];
      const scenarioNames: string[] = [];

      if (inputMode === "wellList") {
        const result = await resolveStickJobEntities(
          values[FIELD_KEYS.wellList],
          values[FIELD_KEYS.stickList],
          getValidWells,
          getValidSticks
        );

        if ("error" in result) {
          setErrors(result.error);
          return;
        }
        validWells = result.wellList;
        stickList = result.stickList;
      } else {
        if (!values.savedFilterId) {
          setErrors("A saved filter is required.");
          return;
        }
        savedFilterId = values.savedFilterId;
        const savedFilter = savedFilters?.find((f) => f.id === savedFilterId);
        savedFilterJson = savedFilter?.filterJSON;
        savedFilterName = savedFilter?.name;

        // Resolve sticks from selected scenarios
        if (selectedScenarioIds.length === 0) {
          setErrors("At least one scenario is required.");
          return;
        }

        try {
          const stickResults = await Promise.all(
            selectedScenarioIds.map(async (scenarioOid) => {
              const response = await axios.get<Stick[]>(
                `${STICK_API_URL}/${scenarioOid}/sticks`
              );
              return response.data ?? [];
            })
          );
          stickList = stickResults.flat().map((stick) => stick.id);

          // Resolve scenario display names
          for (const oid of selectedScenarioIds) {
            const scenario = orgScenarios?.find((s) => s._id.$oid === oid);
            scenarioNames.push(scenario?.scenarioId ?? oid);
          }
        } catch {
          setErrors("Failed to resolve sticks from selected scenarios.");
          return;
        }

        if (stickList.length === 0) {
          setErrors("Selected scenarios have no sticks.");
          return;
        }
      }

      const zones =
        values[FIELD_KEYS.zones]?.map((zone) => zone.split(ZONE_MAP_PREFIX)[1]) ?? [];

      const postData: StickCalculationsJob = {
        ...values,
        [FIELD_KEYS.threeDvLeftWidth]: parseFloat(values[FIELD_KEYS.threeDvLeftWidth]),
        [FIELD_KEYS.threeDvRightWidth]: parseFloat(values[FIELD_KEYS.threeDvRightWidth]),
        [FIELD_KEYS.threeDvUpHeight]: parseFloat(values[FIELD_KEYS.threeDvUpHeight]),
        [FIELD_KEYS.threeDvDownHeight]: parseFloat(values[FIELD_KEYS.threeDvDownHeight]),
        [FIELD_KEYS.xdaLeftWidth]: parseFloat(values[FIELD_KEYS.xdaLeftWidth]),
        [FIELD_KEYS.xdaRightWidth]: parseFloat(values[FIELD_KEYS.xdaRightWidth]),
        [FIELD_KEYS.xdaUpHeight]: parseFloat(values[FIELD_KEYS.xdaUpHeight]),
        [FIELD_KEYS.xdaDownHeight]: parseFloat(values[FIELD_KEYS.xdaDownHeight]),
        calculate3DV: values[FIELD_KEYS.calculate3DV] ?? true,
        calculateWellSpacing: values[FIELD_KEYS.calculateWellSpacing] ?? true,
        threeDvDimensions: {
          leftWidth: parseFloat(values[FIELD_KEYS.threeDvLeftWidth]),
          rightWidth: parseFloat(values[FIELD_KEYS.threeDvRightWidth]),
          upHeight: parseFloat(values[FIELD_KEYS.threeDvUpHeight]),
          downHeight: parseFloat(values[FIELD_KEYS.threeDvDownHeight])
        },
        xdaDimensions: {
          leftWidth: parseFloat(values[FIELD_KEYS.xdaLeftWidth]),
          rightWidth: parseFloat(values[FIELD_KEYS.xdaRightWidth]),
          upHeight: parseFloat(values[FIELD_KEYS.xdaUpHeight]),
          downHeight: parseFloat(values[FIELD_KEYS.xdaDownHeight])
        },
        zones,
        jobId: uuidv4(),
        wellList: validWells,
        stickList,
        savedFilterId: savedFilterId ?? "",
        savedFilterJson,
        savedFilterName,
        ...(inputMode === "savedFilter" && {
          scenarioIds: selectedScenarioIds,
          scenarioNames
        }),
        email
      };

      await mutateAsync(postData);

      setErrors("");
      onClose && onClose();
    } catch (e) {
      const body = (e as AxiosError)?.response?.data;
      setErrors(
        typeof body === "string" && body ? body : "Error occurred while creating job"
      );
      return;
    }
  };

  const requiredRule = { required: true, message: "Required" };

  return (
    <RootContainer>
      <Modal
        open={isModalVisible}
        onOk={handleOk}
        onCancel={onClose}
        title={
          <ModalTitleRow>
            <span>Add New Stick Calculation Job</span>
            <ToggleWrapper>
              <SegmentedToggle
                data-testid="use-saved-filter-toggle"
                value={inputMode}
                options={[
                  { label: "Well List", value: "wellList" },
                  { label: "Saved Filter", value: "savedFilter" }
                ]}
                onChange={(val) => setInputMode(val as "wellList" | "savedFilter")}
                block
                size="large"
                height={34}
              />
            </ToggleWrapper>
          </ModalTitleRow>
        }
        closable={false}
        maskClosable={false}
        width={"700px"}>
        <Form
          name="edit"
          form={form}
          initialValues={{
            remember: true,
            leftWidth: 300,
            rightWidth: 300,
            upHeight: 50,
            downHeight: 50
          }}
          onFinish={handleOk}
          requiredMark={false}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name={FIELD_KEYS.name}
                label={<Label>Name:</Label>}
                colon={false}
                rules={[requiredRule]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={9}>
              <Form.Item
                name={FIELD_KEYS.threeDvLeftWidth}
                label={<Label>3DV Radius ({getDisplayUnit()}):</Label>}
                colon={false}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber precision={0} style={{ width: "100%" }} placeholder="Left" />
              </Form.Item>
            </Col>

            <Col span={5}>
              <Form.Item
                name={FIELD_KEYS.threeDvRightWidth}
                // label={<Label>Right</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Right"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name={FIELD_KEYS.threeDvUpHeight}
                // label={<Label>Up</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber precision={0} style={{ width: "100%" }} placeholder="Up" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name={FIELD_KEYS.threeDvDownHeight}
                // label={<Label>Down</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber precision={0} style={{ width: "100%" }} placeholder="Down" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={9}>
              <Form.Item
                name={FIELD_KEYS.xdaLeftWidth}
                label={<Label>XDA Radius ({getDisplayUnit()}):</Label>}
                colon={false}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber precision={0} style={{ width: "100%" }} placeholder="Left" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name={FIELD_KEYS.xdaRightWidth}
                // label={<Label>Right</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Right"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name={FIELD_KEYS.xdaUpHeight}
                // label={<Label>Up</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber precision={0} style={{ width: "100%" }} placeholder="Up" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name={FIELD_KEYS.xdaDownHeight}
                // label={<Label>Down</Label>}
                rules={[requiredRule]}
                getValueFromEvent={(value) => convertBack(value, -1)}
                getValueProps={(value) => ({
                  value: value != null ? convert(value, 0) : value
                })}>
                <InputNumber precision={0} style={{ width: "100%" }} placeholder="Down" />
              </Form.Item>
            </Col>
          </Row>
          {inputMode === "wellList" ? (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name={FIELD_KEYS.wellList}
                  label={<Label>Well List:</Label>}
                  colon={false}>
                  <TextArea autoSize={{ minRows: 10, maxRows: 10 }} />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="savedFilterId"
                  label={<Label>Saved Filter:</Label>}
                  colon={false}>
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label?.toString() ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    data-testid="saved-filter-select"
                    placeholder="Select a Saved Filter"
                    options={(() => {
                      const createFilterGroups = (
                        scopeName: string,
                        filters: SavedFilterModel[],
                        isSystemScope: boolean
                      ) => {
                        const projectGroups = new Map<string, SavedFilterModel[]>();
                        filters.forEach((filter) => {
                          const projectKey = filter.projectName || "All Projects";
                          const existing = projectGroups.get(projectKey);
                          if (!existing) {
                            projectGroups.set(projectKey, [filter]);
                          } else {
                            existing.push(filter);
                          }
                        });
                        return Array.from(projectGroups.entries())
                          .sort(([a], [b]) => {
                            if (a === "All Projects") return -1;
                            if (b === "All Projects") return 1;
                            return a.localeCompare(b);
                          })
                          .map(([projectName, projectFilters]) => {
                            let label: string;
                            if (isSystemScope) {
                              label = "System Filters";
                            } else if (projectName === "All Projects") {
                              label = scopeName;
                            } else {
                              label = `Project › ${projectName}`;
                            }
                            return {
                              label,
                              options: projectFilters.map((filter) => ({
                                value: filter.id,
                                label: filter.name
                              }))
                            };
                          });
                      };

                      const systemFiltersData = (savedFilters ?? []).filter(
                        (f) => f.filterScope === FilterScope.System
                      );
                      const orgFiltersData = (savedFilters ?? []).filter(
                        (f) => f.filterScope === FilterScope.Org
                      );
                      const teamFiltersData = (savedFilters ?? []).filter(
                        (f) => f.filterScope === FilterScope.Team
                      );
                      const personalFiltersData = (savedFilters ?? []).filter(
                        (f) => f.filterScope === FilterScope.Personal
                      );

                      const options = [];
                      if (systemFiltersData.length > 0) {
                        options.push(
                          ...createFilterGroups("System", systemFiltersData, true)
                        );
                      }
                      if (orgFiltersData.length > 0) {
                        options.push(
                          ...createFilterGroups("Organization", orgFiltersData, false)
                        );
                      }
                      if (teamFiltersData.length > 0) {
                        options.push(
                          ...createFilterGroups("Team", teamFiltersData, false)
                        );
                      }
                      if (personalFiltersData.length > 0) {
                        options.push(
                          ...createFilterGroups("Personal", personalFiltersData, false)
                        );
                      }
                      return options;
                    })()}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {inputMode === "wellList" ? (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name={FIELD_KEYS.stickList}
                  label={<Label>Stick List:</Label>}
                  colon={false}>
                  <TextArea autoSize={{ minRows: 10, maxRows: 10 }} />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label={<Label>Scenarios:</Label>} colon={false}>
                  <Select
                    mode="multiple"
                    data-testid="scenario-select"
                    placeholder="Select scenarios"
                    loading={isScenariosLoading}
                    value={selectedScenarioIds}
                    onChange={setSelectedScenarioIds}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label?.toString() ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={scenarioOptions}
                    maxTagCount={5}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="primarySource" label={<Label>Survey Source</Label>}>
                <PrimarySourceSelect />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="modelSource"
                label={<Label>Geo Source</Label>}
                colon={false}>
                <Select
                  data-testid="model-source-select"
                  value={modelSource}
                  defaultValue={modelSource}
                  options={geoSourceOptions}
                  onChange={(e) => {
                    setModelSource(e);
                    // setSelectedIpdbZones([]);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="zones" label={<Label>Zones</Label>}>
                <TreeSelect
                  showSearch
                  data-testid="geo-zone-select"
                  switcherIcon={
                    <SwitcherIcon>{<Icon path={mdiChevronDown} size={1} />}</SwitcherIcon>
                  }
                  value={selectedIpdbZones}
                  dropdownStyle={{ overflow: "auto" }}
                  placeholder="Please select"
                  allowClear
                  multiple
                  maxTagCount={10}
                  onChange={setSelectedIpdbZones}
                  treeDefaultExpandedKeys={periodKeys}
                  treeCheckable={true}
                  treeData={treeData}
                />
              </Form.Item>
            </Col>
          </Row> */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name={FIELD_KEYS.calculate3DV}
                label={<Label>Sampled 3D:</Label>}
                colon={false}>
                <Switch checked={includeSampled3D == null || includeSampled3D === true} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name={FIELD_KEYS.calculateWellSpacing}
                label={<Label>Spacing:</Label>}
                colon={false}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          {/* //don't use project and filter for now, only accept well list

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="selectedProject" label={<Label>Project</Label>}>
                <Select
                  options={(projects ?? []).map((project) => ({
                    label: project.projectName,
                    value: project.projectId
                  }))}
                  onChange={(val) => {
                    const found = projects.findIndex((p) => p.projectId == val);
                    if (found > -1) {
                      updateSavedFilters(projects[found].projectId);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="savedFilterId"
                label={
                  <Label>
                    {" "}
                    <LabelWithTooltip>
                      <Label>Saved Filter</Label>
                      <Tooltip title="The selected filter determines which wells are included in the calculation">
                        <Icon path={mdiInformation} size={1} />
                      </Tooltip>
                    </LabelWithTooltip>
                  </Label>
                }
                rules={[requiredRule]}>
                <Select
                  options={(savedFilters ?? []).map((filter) => ({
                    value: filter.id,
                    label: filter.name
                  }))}></Select>
              </Form.Item>
            </Col>
          </Row>
          <Alert message={"Only filters with well list or drawn polygon are shown."} />*/}
        </Form>

        {errors && (
          <ErrorMessage
            type="error"
            message={`${errors || "Error occurred while creating job"}`}
          />
        )}
      </Modal>
    </RootContainer>
  );
};

const RootContainer = styled.div``;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-right: 32px;
`;

const ToggleWrapper = styled.div`
  width: 220px;
  flex-shrink: 0;
`;

const Label = styled.span`
  display: block;
  width: 110px;
`;

// const SwitcherIcon = styled.span`
//   transform: translateY(2px);
//   color: rgba(var(--color-text-rgb), 0.3);
// `;
const ErrorMessage = styled(Alert)`
  margin-bottom: 1em;
`;

interface CreateAccessTokenModalProps {
  requestShowModal: boolean;
  jobs: StickCalculationsJob[];
  onClose: () => void;
  savedFilters: SavedFilterModel[];
}
